import type { TrainingVideoDto } from '@ukinfo/shared';
import { formatBytes } from '@ukinfo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { OfflineBanner } from '../components/OfflineBanner';
import { PageHeader } from '../components/PageHeader';
import { VideoPlayer } from '../components/training/ExerciseVideos';
import { PlanView } from '../components/training/PlanView';
import { PlanImageButton } from '../components/training/ShareImageButton';
import { Badge, Button, Card, CardBody, EmptyState, ErrorBlock, LoadingBlock } from '../components/ui';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatTime } from '../lib/format';
import {
  offlineUsage,
  removeAllOffline,
  removeOffline,
  sortEntries,
  storedActiveUser,
  useOfflineEntries,
  useOnline,
} from '../lib/offline';
import { offlineStore, offlineSupported, type OfflineEntry } from '../lib/offline-store';

/**
 * "Offline gespeichert": die Einheiten, die dieses Gerät ohne Netz zeigen kann.
 *
 * Die Seite läuft auch ohne Anmeldung der Sitzung - ohne Netz lässt sich die Sitzung nicht prüfen. Dann
 * gilt das Konto, das zuletzt auf diesem Gerät angemeldet war; seine Daten liegen nur hier und verschwinden
 * beim Abmelden. Die Einheit selbst zeigt dieselben Bausteine wie online (PlanView, Skizze zum Abspielen,
 * Vollbild), nur lesend.
 */

function entrySubtitle(entry: OfflineEntry): string {
  const when = entry.startAt
    ? `${formatDateTime(entry.startAt)}${entry.endAt ? `–${formatTime(entry.endAt)}` : ''}`
    : null;
  return [entry.teamName, when, entry.location || null].filter(Boolean).join(' · ');
}

/** Videos aus dem Gerätespeicher; was nicht mitgenommen wurde, steht als Hinweis da. */
function StoredVideos({ userId, videos }: { userId: string; videos: TrainingVideoDto[] }): JSX.Element | null {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const signature = videos.map((video) => video.id).join(',');

  useEffect(() => {
    let active = true;
    const created: string[] = [];
    void (async () => {
      const next: Record<string, string> = {};
      for (const video of videos) {
        const stored = await offlineStore().getVideo(userId, video.id).catch(() => undefined);
        if (stored) {
          const url = URL.createObjectURL(stored.blob);
          created.push(url);
          next[video.id] = url;
        }
      }
      if (active) setUrls(next);
    })();
    return () => {
      active = false;
      for (const url of created) URL.revokeObjectURL(url);
    };
    // `signature` steht für die Liste der Videos.
  }, [userId, signature]);

  if (videos.length === 0) return null;
  return (
    <section aria-label="Videos">
      <h4 className="text-sm font-semibold text-slate-900">{videos.length === 1 ? 'Video' : 'Videos'}</h4>
      <div className="mt-1 space-y-3">
        {videos.map((video) => {
          const url = urls[video.id];
          return url ? (
            <VideoPlayer key={video.id} video={{ ...video, url }} />
          ) : (
            <p key={video.id} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
              {video.title || 'Video'} ({formatBytes(video.sizeBytes)}) wurde nicht mitgenommen – es gibt es nur mit Netz.
            </p>
          );
        })}
      </div>
    </section>
  );
}

function EntryDetail({ entry }: { entry: OfflineEntry }): JSX.Element {
  const offline = {
    exercises: entry.exercises,
    videos: (videos: TrainingVideoDto[]) => <StoredVideos userId={entry.userId} videos={videos} />,
  };
  return (
    <div className="space-y-4">
      <Link to="/offline" className="inline-block text-sm font-medium text-brand-700">← Alle gespeicherten Einheiten</Link>
      <div>
        <h2 className="break-words text-lg font-semibold text-slate-900">{entry.title}</h2>
        <p className="text-sm text-slate-600">{entrySubtitle(entry) || (entry.kind === 'template' ? 'Vorlage' : '')}</p>
        {entry.plan.title && entry.plan.title !== entry.title && (
          <p className="mt-0.5 break-words text-sm font-medium text-slate-800">Einheit: {entry.plan.title}</p>
        )}
        <p className="mt-1 text-xs text-slate-500">Stand: gespeichert am {formatDateTime(entry.savedAt)}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <PlanImageButton plan={entry.plan} subtitle={entrySubtitle(entry) || 'Vorlage'} exercises={entry.exercises} />
      </div>
      <PlanView plan={entry.plan} offline={offline} />
    </div>
  );
}

function EntryList({ userId, entries }: { userId: string; entries: OfflineEntry[] }): JSX.Element {
  const queryClient = useQueryClient();
  const usage = useQuery({ queryKey: ['offline-entries', 'usage', userId], queryFn: () => offlineUsage(userId) });
  const [error, setError] = useState<string | null>(null);
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offline-entries'] });

  const remove = async (entry: OfflineEntry): Promise<void> => {
    if (!window.confirm(`„${entry.title}“ von diesem Gerät entfernen?`)) return;
    try {
      await removeOffline(userId, entry.planId);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Entfernen fehlgeschlagen');
    }
  };
  const removeAll = async (): Promise<void> => {
    if (!window.confirm('Alle offline gespeicherten Einheiten von diesem Gerät entfernen?')) return;
    try {
      await removeAllOffline(userId);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Entfernen fehlgeschlagen');
    }
  };

  const total = (usage.data?.textBytes ?? 0) + (usage.data?.videoBytes ?? 0);
  return (
    <div className="space-y-4">
      {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
      <Card>
        <CardBody>
          <ul className="divide-y divide-slate-100">
            {entries.map((entry) => (
              <li key={entry.key} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <span className="min-w-0">
                  <span className="block break-words font-medium text-slate-900">{entry.title}</span>
                  <span className="block text-xs text-slate-600">{entry.kind === 'template' ? ['Vorlage', entry.teamName].filter(Boolean).join(' · ') : entrySubtitle(entry)}</span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    gespeichert am {formatDate(entry.savedAt)} {formatTime(entry.savedAt)}
                    {entry.auto && <Badge tone="neutral">automatisch</Badge>}
                    {entry.videoIds.length > 0 && <Badge tone="info">{entry.videoIds.length} {entry.videoIds.length === 1 ? 'Video' : 'Videos'}</Badge>}
                  </span>
                </span>
                <span className="flex flex-wrap gap-2">
                  <Link
                    to={`/offline/${entry.planId}`}
                    aria-label={`Öffnen · ${entry.title}`}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-brand-700 sm:min-h-0"
                  >
                    Öffnen
                  </Link>
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => void remove(entry)}>
                    Entfernen<span className="sr-only"> · {entry.title}</span>
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
        <span data-testid="offline-total">Gesamtspeicher auf diesem Gerät: {formatBytes(total)}{usage.data && usage.data.videoBytes > 0 ? ` (davon Videos ${formatBytes(usage.data.videoBytes)})` : ''}</span>
        <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => void removeAll()}>Alle entfernen</Button>
      </div>
    </div>
  );
}

function OfflineContent({ userId }: { userId: string }): JSX.Element {
  const { planId } = useParams();
  const entries = useOfflineEntries(userId);
  if (entries.isLoading) return <LoadingBlock />;
  if (entries.error) return <ErrorBlock error={entries.error} />;
  const list = sortEntries(entries.data ?? []);
  if (planId) {
    const entry = list.find((candidate) => candidate.planId === planId);
    if (entry) return <EntryDetail entry={entry} />;
    return (
      <EmptyState
        title="Diese Einheit ist nicht gespeichert"
        description="Sie wurde entfernt oder liegt nicht auf diesem Gerät."
        action={<Link to="/offline" className="font-medium text-brand-700">Zur Liste</Link>}
      />
    );
  }
  if (list.length === 0) {
    return (
      <EmptyState
        title="Noch nichts gespeichert"
        description="Öffne bei einem Training die Einheit und wähle „Für offline speichern“. Die Trainings der nächsten 48 Stunden werden beim Öffnen der App automatisch gespeichert, wenn du online bist."
      />
    );
  }
  return <EntryList userId={userId} entries={list} />;
}

/** Ohne Netz und ohne Sitzung: schlichter Rahmen, damit die Seite auch dann steht. */
function StandaloneShell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50">
      <OfflineBanner />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-base font-semibold text-slate-900">UK Info</span>
          <Link to="/anmelden" className="text-sm font-medium text-brand-700">Zur Anmeldung</Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-5">{children}</main>
    </div>
  );
}

function Body(): JSX.Element {
  const { user, isLoading } = useAuth();
  const online = useOnline();
  const [stored, setStored] = useState<{ id: string | null } | null>(null);

  useEffect(() => {
    let active = true;
    void storedActiveUser().then((id) => { if (active) setStored({ id }); }).catch(() => { if (active) setStored({ id: null }); });
    return () => { active = false; };
  }, []);

  if (!offlineSupported()) {
    return <EmptyState title="Nicht möglich" description="Dieser Browser kann keine Einheiten offline speichern." />;
  }
  if (user?.isImpersonating) {
    return <EmptyState title="In der Testansicht nicht verfügbar" description="Beende die Testansicht, um die offline gespeicherten Einheiten zu sehen." />;
  }
  const userId = user?.userId ?? stored?.id ?? null;
  if (isLoading || (!user && stored === null)) return <LoadingBlock />;
  if (!userId) {
    return (
      <EmptyState
        title="Nichts gespeichert"
        description={online ? 'Melde dich an und speichere Einheiten für den Platz.' : 'Auf diesem Gerät sind keine Einheiten gespeichert.'}
        action={<Link to="/anmelden" className="font-medium text-brand-700">Zur Anmeldung</Link>}
      />
    );
  }
  return <OfflineContent userId={userId} />;
}

export function OfflinePage(): JSX.Element {
  const { user } = useAuth();
  const content = (
    <div className="space-y-4">
      <PageHeader
        title="Offline gespeichert"
        description="Einheiten, die du am Platz ohne Netz ansehen und abspielen kannst"
        scoped={false}
      />
      <Body />
    </div>
  );
  // Mit Sitzung im gewohnten Rahmen, sonst (ohne Netz) im schlichten.
  return user ? <AppShell>{content}</AppShell> : <StandaloneShell>{content}</StandaloneShell>;
}
